import { NextRouter } from "next/router";
import {
  LocalStorageEnum,
  OrderTypesEnum,
  PagesEnum,
  URL_PARAMS,
} from "../../enums";
import env from "../../env.config";
import LS from "../../LocalStorage";
import {
  Franchise,
  FranchiseConfig,
  OrganizationCustomer,
  Restaurant,
  getPageLinkProps,
} from "../../types";

export const getSortedPages = (
  franchiseConfig: FranchiseConfig,
  ordertype?: OrderTypesEnum
) => {
  const pagesOrder: PagesEnum[] = [
    PagesEnum.Cart,
    PagesEnum.Schedule,
    PagesEnum.Tip,
    PagesEnum.Pay,
  ];

  const pages: PagesEnum[] = [PagesEnum.Cart, PagesEnum.Tip, PagesEnum.Pay];

  if (
    ordertype &&
    franchiseConfig.scheduledOrder &&
    franchiseConfig.scheduledOrder.scheduledOrderTypes?.includes(ordertype) &&
    !LS._get(LocalStorageEnum.SELF_SERVICE)
  ) {
    pages.push(PagesEnum.Schedule);
  }

  pages.sort((a, b) => pagesOrder.indexOf(a) - pagesOrder.indexOf(b));

  return pages;
};

export const getPreviousPage = (
  currentPage: PagesEnum,
  franchiseConfig: FranchiseConfig,
  ordertype?: OrderTypesEnum
) => {
  const pagesOrder = getSortedPages(franchiseConfig, ordertype);
  const currentPageIndex = pagesOrder.indexOf(currentPage);
  if (currentPageIndex > 0) {
    return pagesOrder[currentPageIndex - 1];
  }
};

export const getNextPage = (
  currentPage: PagesEnum,
  franchiseConfig: FranchiseConfig,
  ordertype?: OrderTypesEnum
) => {
  const pagesOrder = getSortedPages(franchiseConfig, ordertype);
  const currentPageIndex = pagesOrder.indexOf(currentPage);
  if (currentPageIndex < pagesOrder.length - 1) {
    return pagesOrder[currentPageIndex + 1];
  }
};

export const addToParamListIfNotUndefined = (
  paramList: string[],
  urlParam: URL_PARAMS,
  value: string | undefined | boolean
) => {
  const param =
    value && typeof value !== "boolean" ? `${urlParam}=${value}` : undefined;
  param && paramList.push(param);
};

export const getPageLink = (
  router: NextRouter,
  nextPage: PagesEnum | undefined,
  options?: getPageLinkProps
) => {
  const opt = options || {};
  const {
    as,
    queryParams,
    orderType,
    tableNumber,
    franchiseSlug,
    invalidFranchise,
    outOfZone,
    qrcode,
    partnerLogin,
    receiptId,
    payType,
    resetQueryParams,
    locale,
    source,
    clearTable,
  } = opt;

  interface UrlParams {
    table?: string | undefined;
    order_type?: OrderTypesEnum | undefined;
    key?: string | undefined;
    franchise?: string | undefined | boolean;
    partnerlogin?: string | undefined | boolean;
    receipt_id?: string | undefined;
    promo?: string | undefined;
  }
  const urlParams: UrlParams = router.query;
  // Check if running in the browser
  const isBrowser = typeof window !== "undefined";

  // Use router.asPath as fallback for server-side rendering
  const searchParams = new URLSearchParams(
    isBrowser ? window.location.search : router.asPath.split("?")[1]
  );

  /**
   * Read a url param from router.query, falling back to the raw query string.
   *
   * router.query is EMPTY on the first client render of a statically optimised
   * page and only fills once the router is ready, so params read from it alone
   * were missing from every link built during that first render. order_type
   * never had the problem because it already read searchParams directly -- so
   * the first render produced "?order_type=takeout-on-table" with no franchise,
   * next prefetched that url, the router became ready, the link re-rendered as
   * "?franchise=smart&order_type=takeout-on-table" and next prefetched again.
   * Two data-route fetches per link, the first for a url nothing navigates to
   * (seen on /cart and /login, whose links mount immediately; the side-menu
   * links mount later and were already single).
   *
   * Reading both sources makes a link identical before and after hydration.
   */
  const paramFromUrl = (key: string): string | undefined => {
    const fromRouter = urlParams[key as keyof UrlParams];
    if (fromRouter !== undefined) {
      return fromRouter as string;
    }

    return searchParams.get(key) ?? undefined;
  };
  let hrefRes = "";
  let asRes = "";
  const paramList: string[] = [];

  if (!resetQueryParams) {
    const newFranchise: string | undefined | boolean =
      franchiseSlug === null
        ? franchiseSlug
        : franchiseSlug === false
        ? franchiseSlug
        : franchiseSlug || paramFromUrl(URL_PARAMS.FRANCHISE);

    addToParamListIfNotUndefined(paramList, URL_PARAMS.FRANCHISE, newFranchise);
    const newTable = clearTable
      ? undefined
      : tableNumber || paramFromUrl(URL_PARAMS.TABLE);
    addToParamListIfNotUndefined(
      paramList,
      URL_PARAMS.TABLE,
      newTable?.toString()
    );
    const newOrderType: any = orderType || searchParams.get("order_type");
    addToParamListIfNotUndefined(
      paramList,
      URL_PARAMS.ORDER_TYPE,
      newOrderType
    );
    addToParamListIfNotUndefined(
      paramList,
      URL_PARAMS.PARNTER_LOGIN,
      partnerLogin || paramFromUrl(URL_PARAMS.PARNTER_LOGIN)
    );

    addToParamListIfNotUndefined(
      paramList,
      URL_PARAMS.PROMO,
      paramFromUrl(URL_PARAMS.PROMO)
    );

    opt.nextPage &&
      addToParamListIfNotUndefined(
        paramList,
        URL_PARAMS.NEXT_PAGE,
        opt.nextPage
      );

    qrcode &&
      addToParamListIfNotUndefined(
        paramList,
        URL_PARAMS.QRCODE,
        qrcode.toString()
      );

    receiptId &&
      addToParamListIfNotUndefined(
        paramList,
        URL_PARAMS.RECEIPT_ID,
        receiptId.toString()
      );

    invalidFranchise &&
      addToParamListIfNotUndefined(
        paramList,
        URL_PARAMS.INVALID_FRANCHISE,
        "true"
      );

    outOfZone &&
      addToParamListIfNotUndefined(paramList, URL_PARAMS.OUT_OF_ZONE, "true");
    payType &&
      addToParamListIfNotUndefined(paramList, URL_PARAMS.PAY_TYPE, payType);
    source &&
      addToParamListIfNotUndefined(paramList, URL_PARAMS.SOURCE, source);
  }

  let params = paramList.join("&");
  if (params.length > 0) {
    params = "?" + params;
  }

  // Add locale prefix if provided
  const localePrefix = locale ? `/${locale}` : "";

  if (nextPage && !as) {
    hrefRes = `${localePrefix}/${nextPage}${params}`;
    asRes = `${localePrefix}/${nextPage}${params}`;
  } else if (nextPage && as) {
    hrefRes = `${localePrefix}/${nextPage}${params}`;
    if (nextPage === PagesEnum.EditItem) {
      asRes = `${localePrefix}/${as}/edit${params}`;
    } else if (nextPage === PagesEnum.Item) {
      asRes = `${localePrefix}/item/${as}${params}`;
    } else {
      asRes = `${localePrefix}/${as}${params}`;
    }
  } else {
    hrefRes = `${localePrefix}/${params}`;
    asRes = `${localePrefix}/${params}`;
  }

  if (queryParams && queryParams.length > 0 && !resetQueryParams) {
    if (params.length === 0) {
      hrefRes += "?";
      asRes += "?";
    } else {
      hrefRes += "&";
      asRes += "&";
    }
    queryParams.forEach((queryParam, index) => {
      hrefRes += `${index ? "&" : ""}${queryParam}`;
      asRes += `${index ? "&" : ""}${queryParam}`;
    });
  }

  return {
    href: hrefRes,
    as: asRes,
  };
};

export const setUrlParam = (key: string, value: string) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(key, value);
  const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
  window.history.pushState({}, "", newUrl);
};

export const removeUrlParam = (key: string) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(key);
  const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
  window.history.pushState({}, "", newUrl);
};

export const getFranchiseSlug = (franchiseParam: string) => {
  if (franchiseParam) {
    return franchiseParam;
  }

  const url = window.location.href;
  const subDomain = url.substring(0, url.indexOf("."));
  // PR preview subdomains follow the pattern "pr-<number>-<name>" and should
  // not be parsed for a franchise slug — only production subdomains are valid.
  const isPrPreview = /pr-\d+-/.test(subDomain);
  const index = subDomain.lastIndexOf("-");
  let franchiseSlug = undefined as unknown as string;
  if (!isPrPreview && index > -1) {
    franchiseSlug = subDomain.substring(subDomain.lastIndexOf("-") + 1);
  }

  return franchiseSlug || franchiseParam;
};

export const checkValidFranchiseInParams = (
  localStorageFranchise?: string,
  paramsFranchise?: string | string[]
) => {
  if (!localStorageFranchise && !paramsFranchise) {
    return true;
  }
  if (!localStorageFranchise || !paramsFranchise) {
    return false;
  }
  paramsFranchise = paramsFranchise.toString();

  return localStorageFranchise === paramsFranchise;
};

export const checkIfCurrentFranchiseSlugCorrect = (
  restaurant: Restaurant,
  franchiseSlug: string
) => {
  if (!restaurant.franchises || restaurant.franchises.length === 0) {
    return true;
  }
  const currentFranchise = restaurant.franchises.find(
    (fr) => fr.slug === franchiseSlug
  );

  return (
    (currentFranchise && currentFranchise.restaurantId === restaurant.id) ||
    !franchiseSlug
  );
};

export const getUrlWithhHttpOrHttps = (url: string) => {
  return `${env.SSLHttpOrHttps}://${url}`;
};

export const handleRedirectToHomepagePartnerApp = (
  organizationCustomer: OrganizationCustomer | null
) => {
  if (
    organizationCustomer?.organization &&
    organizationCustomer != null &&
    organizationCustomer?.verified
  ) {
    let url = `${organizationCustomer.organization.slug}.${env.organizationUrl}`;

    if (env.enviroment === "local") {
      url = `${env.organizationUrl}/?slug=${organizationCustomer.organization.slug}`;
    }

    window.location.href = getUrlWithhHttpOrHttps(url);
  }
};

export const filterFranchisesByDirectLinkAccess = (
  franchises: Franchise[],
  currentFranchiseSlug?: string
) => {
  const currentFranchise = franchises.find(
    (f) => f.slug === currentFranchiseSlug
  );

  return franchises.filter((fr: Franchise) => {
    if (currentFranchiseSlug) {
      if (fr.slug === currentFranchiseSlug) {
        return true;
      } else {
        // If the franchise we're accessing has onlyAccessibleFromDirectLink flag,
        // don't show any other franchises
        if (currentFranchise?.franchiseConfig?.onlyAccessibleFromDirectLink) {
          return false;
        }

        return !fr.franchiseConfig?.onlyAccessibleFromDirectLink;
      }
    } else {
      return !fr.franchiseConfig?.onlyAccessibleFromDirectLink;
    }
  });
};
